// Cross-device preferences (phase 25). Money mode changes what every
// invoice/expense figure MEANS, so it must not differ between Henry's desk
// and his phone: D1 (settings table, key 'money_mode') is authoritative,
// the client's localStorage is only a first-paint cache. Theme deliberately
// stays device-local.

export type MoneyMode = 'ex' | 'incl';

export const SIDEBAR_GROUPS = ['work', 'growth', 'money', 'tools'] as const;
export type SidebarGroup = (typeof SIDEBAR_GROUPS)[number];
export type SidebarLayout = Record<SidebarGroup, string[]>;

/** Validate an untrusted value (query body, settings row, localStorage). */
export function normalizeMoneyMode(value: unknown): MoneyMode | null {
  return value === 'ex' || value === 'incl' ? value : null;
}

/** Validate and copy an untrusted sidebar layout from JSON or localStorage. */
export function normalizeSidebarLayout(value: unknown): SidebarLayout | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;

  const record = value as Record<string, unknown>;
  const hasGrowth = Object.prototype.hasOwnProperty.call(record, 'growth');
  const seen = new Set<string>();
  const layout: SidebarLayout = { growth: [], work: [], money: [], tools: [] };

  for (const group of SIDEBAR_GROUPS) {
    const items = group === 'growth' && !hasGrowth ? [] : record[group];
    if (!Array.isArray(items)) return null;
    for (const item of items) {
      if (typeof item !== 'string' || item.length === 0 || seen.has(item)) return null;
      seen.add(item);
      layout[group].push(item);
    }
  }

  return hasGrowth ? layout : migrateGrowthLayout(layout);
}

/** One migration for both D1 prefs and the device cache. Preserve all other order. */
export function migrateGrowthLayout(layout: SidebarLayout): SidebarLayout {
  const growthIds = ['/leads', '/marketing', '/outreach'];
  return Object.fromEntries(SIDEBAR_GROUPS.map(group => [group,
    [...(group === 'growth' ? growthIds : []), ...layout[group].filter(id => !growthIds.includes(id))],
  ])) as SidebarLayout;
}
